import { GetListParams, GetOneParams } from 'react-admin';
import { CustomDataProvider } from '../../providers/CustomDataProvider';
import { baseUrl, httpClient } from '../../providers/AppDataProvider';
import { AppVersionFormValues } from './types';
import { toApiPayload } from './utils';

const RESOURCE = 'app-version-management';
const API_PATH = 'app-version-info';

export class AppVersionDataProvider implements CustomDataProvider {
  // eslint-disable-next-line class-methods-use-this
  async getList(_resource: string, params: GetListParams): Promise<any> {
    const { page = 1, perPage = 20 } = params.pagination || {};

    const searchParams = new URLSearchParams({
      page: String(page),
      pageSize: String(perPage),
      orderUpdatedAtDesc: 'true',
    });

    const { filter = {} } = params;
    if (filter.platform && filter.platform !== 'ALL') {
      searchParams.set('platform', filter.platform);
    }

    const url = `${baseUrl}/${API_PATH}?${searchParams.toString()}`;
    const { json } = await httpClient(url);

    const rows: any[] = json?.data ?? json?.list ?? json?.items ?? json?.records ?? [];
    const total = json?.total ?? json?.count ?? rows.length;
    const data = rows.map((r: any, i: number) => (r?.id != null ? r : { id: `${page}-${i}`, ...r }));

    return { data, total };
  }

  // eslint-disable-next-line class-methods-use-this
  async getOne(_resource: string, params: GetOneParams): Promise<any> {
    const url = `${baseUrl}/${API_PATH}/${params.id}`;
    const { json } = await httpClient(url);
    const data = json?.data ?? json;
    return { data: { id: params.id, ...data } };
  }

  // eslint-disable-next-line class-methods-use-this
  async create(_resource: string, params: { data: AppVersionFormValues }): Promise<any> {
    const url = `${baseUrl}/${API_PATH}`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(toApiPayload(params.data)),
    });
    const data = json?.data ?? json;
    return { data: { id: data?.id ?? Date.now(), ...data } };
  }

  // eslint-disable-next-line class-methods-use-this
  async update(_resource: string, params: { id: string | number; data: AppVersionFormValues }): Promise<any> {
    const url = `${baseUrl}/${API_PATH}/${params.id}`;
    const { json } = await httpClient(url, {
      method: 'PUT',
      body: JSON.stringify(toApiPayload(params.data)),
    });
    const data = json?.data ?? json;
    return { data: { id: params.id, ...data } };
  }

  // eslint-disable-next-line class-methods-use-this
  async delete(_resource: string, params: { id: string | number }): Promise<any> {
    const url = `${baseUrl}/${API_PATH}/${params.id}`;
    const { json } = await httpClient(url, { method: 'DELETE' });
    return { data: { id: params.id, ...(json?.data ?? json) } };
  }
}

export const appVersionDataProvider = new AppVersionDataProvider();

export async function activateVersion(id: string | number): Promise<void> {
  const url = `${baseUrl}/${API_PATH}/${id}/active`;
  await httpClient(url, {
    method: 'PATCH',
    body: JSON.stringify({ active: true }),
  });
}

export { RESOURCE };
